import React from 'react'

import classNames from 'classnames'
import styled from 'styled-components'
import {palette} from 'styled-tools'
import {media} from 'styled-bootstrap-grid'
import Image from 'next/image'

import {PagoLoanCalculation} from '@festi/common/api/rest'
import {formatPercentage, formatPrice} from '@festi/utils/numbers'
import {formatRestDate} from '@festi/common/utils/dates'
import {styledTheme} from '@festi/common/themes'
import {PMedium} from '@festi/common/components/typography'

const Grid = styled.div`
  display: grid;
  width: 100%;
  padding-inline: 16px;
  padding-block: 4px;
  border-radius: 8px;
  background-color: ${palette('ui4Solid')};
  color: ${palette('ui60Solid')};

  &.blurText {
    color: transparent;
    text-shadow: 0 0 6px ${palette('blue')};
  }

  ${media.md`
    padding-inline: 24px;
    padding-block: 8px;
  `}
`

const Start = styled.div`
  position: relative;
  justify-self: start;
  display: flex;
  flex-direction: row;
  align-items: flex-end;
`
const End = styled.div`
  justify-self: end;
`

const Row = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding-block: 8px;
  border-bottom: 1px solid ${palette('ui10Solid')};

  &:last-child {
    border-bottom: none;
  }

  &:nth-last-child(2) {
    border-width: 2px;
  }

  &.subCost {
    width: calc(100% - 38px);
    transform: translateX(38px);
  }
`

const TotalPrice = styled.p`
  font-size: 24px;
  font-family: 'ElkjopHeadline', sans-serif;
  color: ${styledTheme.palette.blue};
`

const SubCostIcon = styled(Image)`
  position: absolute;
  bottom: 0;
  left: -24px;
`

interface CalculationDataGridProps {
  loanCalculationData: PagoLoanCalculation
  isLoading: boolean
}

export default function CalculationDataGrid({
  loanCalculationData,
  isLoading,
}: CalculationDataGridProps) {
  const payments = loanCalculationData?.payments || []

  const gridContent = [
    {
      title: 'Verð með lántökugjaldi',
      value: formatPrice(
        Number(loanCalculationData?.amount || 0) +
          Number(loanCalculationData?.loanFeeAmount || 0),
      ),
    },
    {
      title: 'Vöruverð',
      value: formatPrice(loanCalculationData?.amount || 0),
      subCost: true,
    },
    {
      title: 'Lántökugjald',
      value: formatPrice(loanCalculationData?.loanFeeAmount || 0),
      subCost: true,
    },
    {
      title: 'Annar kostnaður',
      value: formatPrice(
        Number(loanCalculationData?.totalInterests || 0) +
          Number(loanCalculationData?.totalFees || 0),
      ),
    },
    {
      title: 'Afborgunargjald',
      value: `${formatPrice(payments[0]?.installmentFeeAmount || 0)}/greiðslu`,
      subCost: true,
    },
    {
      title: 'Greiðslugjald',
      value: `${formatPrice(payments[0]?.paymentFeeAmount || 0)}/greiðslu`,
      subCost: true,
    },
    {
      title: 'Vextir',
      value: `${formatPercentage(loanCalculationData?.interestRate || 0)}%`,
      subCost: true,
    },
    {
      title: 'ÁHK',
      value: `${formatPercentage(loanCalculationData?.APR || 0)}%`,
      subCost: true,
    },
    {
      title: 'Fyrsti gjalddagi',
      value: formatRestDate(payments[0]?.paymentDate),
    },
  ]

  return (
    <Grid className={classNames({blurText: isLoading})}>
      {/* TODO: Collapse sub costs */}
      {gridContent.map((item, index) => (
        <Row key={index} className={classNames({subCost: item.subCost})}>
          <Start>
            {item.subCost && (
              <SubCostIcon
                src={'/icons/sub-cost-icon.svg'}
                alt="Sub cost icon"
                width={12}
                height={12}
              />
            )}
            {item.title}
          </Start>
          <End>{item.value}</End>
        </Row>
      ))}
      <Row>
        <Start>
          <PMedium bold>Heildarverð</PMedium>
        </Start>
        <End>
          <TotalPrice>
            {formatPrice(loanCalculationData?.totalAmount || 0)}
          </TotalPrice>
        </End>
      </Row>
    </Grid>
  )
}
